<template>
  <i-modal
    :title="
      payment.pk
        ? `${$t('edit')} ${$tc('payment', 1)}`
        : `${$t('add', { value: $tc('payment', 1) })}`
    "
    :value="value"
    :load="loading"
    max-width="700px"
    @submit="submit"
    @input="$emit('input', $event)"
  >
    <ValidationObserver ref="formObserver">
      <v-row class="my-0">
        <!-- Fecha -->
        <v-col cols="12" sm="6">
          <i-date-picker
            v-model="payment.date"
            :label="$t('date')"
            color="primary"
          />
        </v-col>

        <!-- Operador -->
        <v-col cols="12" sm="6">
          <ValidationProvider
            vid="operator"
            :name="$t('operator')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="payment.operator"
              outlined
              dense
              :error-messages="errors[0]"
              :label="$t('operator')"
              color="secondary"
            />
          </ValidationProvider>
        </v-col>

        <!-- Monto a pagar -->
        <v-col cols="12" sm="6">
          <ValidationProvider
            vid="payment"
            :name="$tc('payment_amount', 1)"
            rules="required|min_value:0.01"
            v-slot="{ errors }"
          >
            <i-money
              mode="input"
              outlined
              dense
              v-model="payment.payment"
              :error-messages="errors[0]"
              :label="$tc('payment_amount', 1)"
              color="secondary"
            />
          </ValidationProvider>
        </v-col>

        <!-- Método de pago -->
        <v-col cols="12" sm="6">
          <ValidationProvider
            vid="pay"
            :name="$t('paymenttype')"
            v-slot="{ errors }"
          >
            <v-select
              outlined
              dense
              v-model="payment.pay"
              :error-messages="errors[0]"
              item-text="state"
              item-value="abbr"
              :items="paymentTypes"
              :label="$t('paymenttype')"
            />
          </ValidationProvider>
        </v-col>

        <!-- Referencia -->
        <v-col cols="12" sm="6">
          <ValidationProvider
            vid="reference"
            :name="$tc('reference', 1)"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="payment.reference"
              outlined
              dense
              :error-messages="errors[0]"
              :label="$tc('reference', 1)"
              color="secondary"
            />
          </ValidationProvider>
        </v-col>

        <!-- Descripción -->
        <v-col cols="12" sm="6">
          <ValidationProvider
            vid="description"
            :name="$tc('description', 1)"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="payment.description"
              outlined
              dense
              :error-messages="errors[0]"
              :label="$tc('description', 1)"
              color="secondary"
            />
          </ValidationProvider>
        </v-col>
      </v-row>
    </ValidationObserver>
  </i-modal>
</template>

<script>
/**
 * Componente modal para crear y editar pagos vinculados a un mantenimiento.
 *
 * @author Pedro Contreras <pdrocont@gmail.com>
 * @date 2026-09-25
 */
export default {
  name: 'dialMaintenancePayment',
  props: {
    /** Controla la visibilidad del modal */
    value: {
      type: Boolean,
      default: false
    },
    /** Objeto de pago en edición o null para creación */
    toEdit: {
      type: Object,
      default: () => null
    },
    /** ID del mantenimiento padre */
    maintenanceId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      payment: {
        pk: null,
        maintenance_id: null,
        date: new Date().toISOString().substr(0, 10),
        operator: '',
        payment: 0.0,
        pay: 'cash',
        reference: '',
        description: ''
      },
      paymentTypes: [
        { state: this.$t('cash'), abbr: 'cash' },
        { state: this.$t('cc'), abbr: 'cc' },
        { state: this.$t('check'), abbr: 'check' }
      ]
    }
  },
  watch: {
    /**
     * Sincroniza los datos al abrir o cerrar el modal
     *
     * @param {Boolean} val Estado del modal
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-25
     */
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.initForm()
        }
      }
    }
  },
  methods: {
    /**
     * Inicializa los datos del formulario de pago
     *
     * @returns {Void}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-25
     */
    initForm() {
      if (this.toEdit) {
        this.payment = {
          pk: this.toEdit.pk,
          maintenance_id: this.maintenanceId,
          date: this.toEdit.date || new Date().toISOString().substr(0, 10),
          operator: this.toEdit.operator || '',
          payment: parseFloat(this.toEdit.payment) || 0.0,
          pay: this.toEdit.pay || 'cash',
          reference: this.toEdit.reference || '',
          description: this.toEdit.description || ''
        }
      } else {
        this.payment = {
          pk: null,
          maintenance_id: this.maintenanceId,
          date: new Date().toISOString().substr(0, 10),
          operator: '',
          payment: 0.0,
          pay: 'cash',
          reference: '',
          description: ''
        }
      }
      this.$nextTick(() => {
        if (this.$refs.formObserver) {
          this.$refs.formObserver.reset()
        }
      })
    },

    /**
     * Envía la creación o edición del pago del mantenimiento
     *
     * @returns {Promise<Void>}
     * @author Pedro Contreras <pdrocont@gmail.com>
     * @date 2026-09-25
     */
    async submit() {
      if (this.$refs.formObserver) {
        const isValid = await this.$refs.formObserver.validate()
        if (!isValid) return
      }

      const paymentVal = parseFloat(this.payment.payment)
      if (isNaN(paymentVal) || paymentVal <= 0) {
        if (this.$refs.formObserver) {
          this.$refs.formObserver.setErrors({
            payment: [
              this.$tc('payment_amount', 1) +
                ' ' +
                (this.$t('validation.min_value', { min: 0.01 }) ||
                  'debe ser mayor a 0')
            ]
          })
        }
        return
      }

      this.loading = true
      try {
        const payload = {
          maintenance_id: this.maintenanceId,
          date: this.payment.date,
          operator: this.payment.operator,
          payment: paymentVal,
          pay: this.payment.pay,
          reference: this.payment.reference,
          description: this.payment.description
        }

        let res
        if (this.payment.pk) {
          res = await this.$api.maintenance.payment.edit({
            pk: this.payment.pk,
            form: payload
          })
        } else {
          res = await this.$api.maintenance.payment.create({
            form: payload
          })
        }

        this.$toast.success(
          `${this.$tc('payment', 1)} ${this.$tc(
            this.payment.pk ? 'edited' : 'created',
            1
          )}`
        )
        this.$emit('done', res.data)
        this.$emit('input', false)
      } catch (error) {
        console.error('Error guardando pago:', error)
        if (
          error.response &&
          error.response.status === 400 &&
          this.$refs.formObserver
        ) {
          this.$refs.formObserver.setErrors(error.response.data)
        } else {
          this.$toast.error(this.$t('required'))
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
